import { useCallback, useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useLocation } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import * as R from 'ramda';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  similaritiesGetDataById,
  similaritiesGetFetchSettingByCode,
  similaritiesSetPersonId,
} from 'SimilaritiesPackage/MainModule/actions/similaritiesActions';
import { getSimilaritiesDataById } from 'SimilaritiesPackage/MainModule/selectors/similaritiesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { ISimilarityMatch } from 'APIPackage/SimilarityModule/interfaces/ISimilarityMatch';
import { Table } from 'CorePackage/TableModule/components/Table';

import { SimilaritiesByPersonHeader } from './SimilaritiesByPersonHeader';
import { SimilaritiesByPersonFiltersModal } from './SimilaritiesByPersonFiltersModal';
import { SimilaritiesByPersonDateModal } from './SimilaritiesByPersonDateModal';
import { SimilaritiesByPersonPanel } from './SimilaritiesByPersonPanel';
import styles from './SimilaritiesByPerson.module.scss';
import { similaritiesByPersonTableHeaders } from '../constants/similaritiesByPersonTableHeaders';
import columnStyles from './SimilaritiesByPersonTable.module.scss';

const parseDate = (date: string) => date.split('-').reverse().join('.');

interface ISimilaritiesByPersonProps {
  similaritiesById: ISimilarityMatch[];
}

const SimilaritiesByPersonWithoutStore = ({ similaritiesById }: ISimilaritiesByPersonProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isSimilaritiesDateModalVisible, setIsSimilaritiesDateModalVisible] = useState(false);
  const [knmDateOptions, setKnmDateOptions] = useState<ISelectOption[]>([]);
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});
  const [activeIds, setActiveIds] = useState<string[] | number[]>([]);
  const [title, setTitle] = useState('');

  const location = useLocation();

  const onFilterModalClose = useCallback(() => setIsFiltersModalVisible(false), []);
  const onSimilaritiesDateModalClose = useCallback(() => setIsSimilaritiesDateModalVisible(false), []);
  const personId = location.pathname.split('/')[3];

  useEffectOnce(() => {
    setTitle(decodeURI(location.search.slice(1)));
  });

  useEffect(() => {
    dispatch(similaritiesSetPersonId(personId));
    dispatch(similaritiesGetDataById());
    dispatch(similaritiesGetFetchSettingByCode(AdminSettingCodesEnum.KNM_MATCH_EXPORT_DATE));
  }, [dispatch, personId]);

  useEffect(() => {
    const newItemsCheck: TableCheckItems = {};

    similaritiesById.forEach(item => {
      newItemsCheck[item.id] = false;
    });
    setItemsCheck(newItemsCheck);
  }, [similaritiesById]);

  useEffect(() => {
    setKnmDateOptions(
      R.uniq(
        similaritiesById
          .filter(simil => itemsCheck[simil.id])
          .map(simil => ({
            id: parseDate(simil.startDate),
            label: parseDate(simil.startDate),
            value: parseDate(simil.startDate),
          })),
      ),
    );
  }, [itemsCheck, similaritiesById, setKnmDateOptions]);

  return (
    <>
      <SimilaritiesByPersonHeader title={title} />
      <div className={styles.SimilaritiesPage}>
        <SimilaritiesByPersonPanel
          setFiltersModalVisible={setIsFiltersModalVisible}
          itemsCheck={itemsCheck}
          setSimilaritiesDateModalVisible={setIsSimilaritiesDateModalVisible}
          activeIds={activeIds}
          setActiveIds={setActiveIds}
        />
        <Table<ISimilarityMatch>
          columns={similaritiesByPersonTableHeaders}
          itemsCheck={itemsCheck}
          data={similaritiesById}
          setItemsCheck={setItemsCheck}
          columnStyles={columnStyles}
          headPlacement="centre"
          checkedNames={['КНМ', 'КНМ', 'КНМ']}
        />
      </div>
      <SimilaritiesByPersonFiltersModal isVisible={isFiltersModalVisible} onClose={onFilterModalClose} />
      <SimilaritiesByPersonDateModal
        isVisible={isSimilaritiesDateModalVisible}
        onClose={onSimilaritiesDateModalClose}
        knmDateOptions={knmDateOptions}
        activeIds={activeIds}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  similaritiesById: getSimilaritiesDataById(state),
});

export const SimilaritiesByPerson = connect(mapStateToProps)(SimilaritiesByPersonWithoutStore);
